import React, { useState, useRef, useCallback } from 'react';
import { API_BASE } from './PanelIcons';

// ─── Constants ────────────────────────────────────────────────────────────────

const LANG_BTNS = [
  { id: 'english',     label: 'English' },
  { id: 'teluguRoman', label: 'Telugu' },
  { id: 'hindiRoman',  label: 'Hindi'  },
  { id: 'telugu',      label: 'తెలుగు' },
];

const QUALITY = {
  good:    { bg: '#14532d', border: '#166534', text: '#86efac', dot: '#4ade80', label: 'Strong'      },
  average: { bg: '#78350f', border: '#92400e', text: '#fcd34d', dot: '#fbbf24', label: 'Average'     },
  bad:     { bg: '#7f1d1d', border: '#991b1b', text: '#fca5a5', dot: '#f87171', label: 'Challenging' },
};

const PLANET_ICONS = {
  SUN:'☀️', MOON:'🌙', MARS:'♂️', MERCURY:'☿️',
  JUPITER:'♃', VENUS:'♀️', SATURN:'♄', RAHU:'☊', KETU:'☋',
};

// ─── Geocode using Nominatim ──────────────────────────────────────────────────

async function geocodePlace(place) {
  const INDIA = { lat: 17.3850, lon: 78.4867, tzone: 5.5 };
  if (!place.trim()) return INDIA;
  try {
    const r    = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(place)}&format=json&limit=1`);
    const data = await r.json();
    if (Array.isArray(data) && data[0]) {
      const lat   = parseFloat(data[0].lat);
      const lon   = parseFloat(data[0].lon);
      const tzone = (lon >= 60 && lon <= 100) ? 5.5 : Math.round(lon / 15 * 2) / 2;
      return { lat, lon, tzone, display: data[0].display_name?.split(',').slice(0, 3).join(', ') };
    }
  } catch {}
  return INDIA;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const QualityBadge = ({ quality }) => {
  const q = QUALITY[quality] || QUALITY.average;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: q.bg, border: `1px solid ${q.border}`,
      color: q.text, borderRadius: 4, padding: '1px 7px',
      fontSize: 11, fontWeight: 600,
    }}>
      <span style={{ color: q.dot }}>●</span> {q.label}
    </span>
  );
};

const HousePill = ({ house, overlap }) => (
  <span style={{
    display: 'inline-block',
    background: overlap ? '#1e3a5f' : '#1a1a2e',
    border: `1px solid ${overlap ? '#3b82f6' : '#374151'}`,
    color: overlap ? '#93c5fd' : '#9ca3af',
    borderRadius: 3, padding: '1px 6px', fontSize: 11, margin: '1px 2px',
  }}>
    H{house}{overlap ? ' ★' : ''}
  </span>
);

const PlanetScoresTable = ({ scores }) => {
  if (!scores) return null;
  const planets = Object.values(scores);
  return (
    <div style={{ overflowX: 'auto', marginTop: 10 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #374151' }}>
            {['Planet', 'Score', 'Quality', 'Functional', 'Retro'].map(h => (
              <th key={h} style={{ padding: '4px 8px', color: '#9ca3af', fontWeight: 500, textAlign: 'left' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {planets.map(p => {
            const q = QUALITY[p.normalized] || QUALITY.average;
            return (
              <tr key={p.planet} style={{ borderBottom: '1px solid #1f2937' }}>
                <td style={{ padding: '4px 8px', color: '#e5e7eb' }}>
                  {PLANET_ICONS[p.planet] || ''} {p.planet}
                </td>
                <td style={{ padding: '4px 8px', color: q.text, fontWeight: 600 }}>{p.score > 0 ? `+${p.score}` : p.score}</td>
                <td style={{ padding: '4px 8px' }}><QualityBadge quality={p.normalized} /></td>
                <td style={{ padding: '4px 8px', color: '#d1d5db' }}>{p.functionalNature}</td>
                <td style={{ padding: '4px 8px', color: p.isRetro ? '#fbbf24' : '#6b7280' }}>{p.isRetro ? '↺ R' : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const LifeEventBadge = ({ event, lang }) => {
  const text = event[lang] || event.english || event.teluguRoman || '';
  const conf = event.confidence || 0;
  const color = conf >= 70 ? '#14532d' : conf >= 50 ? '#78350f' : '#1e3a5f';
  const border = conf >= 70 ? '#166534' : conf >= 50 ? '#92400e' : '#1e40af';
  const textColor = conf >= 70 ? '#86efac' : conf >= 50 ? '#fcd34d' : '#93c5fd';
  return (
    <div style={{
      background: color, border: `1px solid ${border}`, borderRadius: 5,
      padding: '6px 10px', marginBottom: 5,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
        <span style={{ color: textColor, fontSize: 11, fontWeight: 700 }}>
          ◆ {event.name}
        </span>
        <span style={{
          background: 'rgba(0,0,0,0.3)', color: textColor, fontSize: 10,
          padding: '1px 6px', borderRadius: 3, fontWeight: 600,
        }}>
          {conf}% match
        </span>
        <span style={{ color: '#6b7280', fontSize: 10, textTransform: 'uppercase' }}>
          {event.category}
        </span>
      </div>
      {text && (
        <div style={{ color: '#d1d5db', fontSize: 12, lineHeight: 1.6 }}>{text}</div>
      )}
    </div>
  );
};

const AntardashaBlock = ({ ad, lang, isCurrent }) => {
  const [open, setOpen] = useState(isCurrent);
  const q = QUALITY[ad.combinedQuality] || QUALITY.average;
  const text = ad.text?.[lang] || ad.text?.english || '';

  return (
    <div style={{
      margin: '4px 0 4px 24px',
      border: `1px solid ${isCurrent ? '#2563eb' : '#374151'}`,
      borderRadius: 5,
      background: isCurrent ? '#0f172a' : '#111827',
      overflow: 'hidden',
    }}>
      {/* Antardasha header */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '7px 10px', cursor: 'pointer',
          background: isCurrent ? '#1e293b' : 'transparent',
          borderBottom: open ? `1px solid ${isCurrent ? '#2563eb' : '#374151'}` : 'none',
        }}
        onClick={() => setOpen(v => !v)}
      >
        <span style={{ color: '#6b7280', fontSize: 11, minWidth: 8 }}>{open ? '▾' : '▸'}</span>
        <span style={{ color: '#d1d5db', fontSize: 12, fontWeight: 500, flex: 1 }}>
          {PLANET_ICONS[ad.mdPlanet] || ''} {ad.mdPlanet} / {PLANET_ICONS[ad.adPlanet] || ''} {ad.adPlanet}
          <span style={{ color: '#6b7280', fontWeight: 400, marginLeft: 6 }}>
            Age {ad.ageStart}–{ad.ageEnd}
          </span>
        </span>

        {isCurrent && (
          <span style={{ background: '#1d4ed8', color: '#bfdbfe', fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>
            CURRENT
          </span>
        )}
        <QualityBadge quality={ad.combinedQuality} />

        {ad.sadeSati && (
          <span style={{ background: '#7f1d1d', color: '#fca5a5', fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>
            ⚠ Sade Sati
          </span>
        )}
        {ad.kantakSani && (
          <span style={{ background: '#78350f', color: '#fde68a', fontSize: 10, padding: '1px 6px', borderRadius: 4 }}>
            ⚡ Kantak
          </span>
        )}
      </div>

      {open && (
        <div style={{ padding: '10px 12px' }}>
          {/* House pills */}
          {ad.activatedHouses?.length > 0 && (
            <div style={{ marginBottom: 8, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
              <span style={{ color: '#6b7280', fontSize: 11, marginRight: 4 }}>Houses:</span>
              {ad.activatedHouses.map(h => (
                <HousePill key={h} house={h} overlap={ad.overlappingHouses?.includes(h)} />
              ))}
              {ad.overlappingHouses?.length > 0 && (
                <span style={{ color: '#60a5fa', fontSize: 10, marginLeft: 4 }}>★ = double-activated</span>
              )}
            </div>
          )}

          {/* Prediction text */}
          <div style={{
            color: '#d1d5db', fontSize: 13, lineHeight: 1.7,
            borderLeft: `2px solid ${q.border}`, paddingLeft: 10, marginBottom: 8,
          }}>
            {text}
          </div>

          {/* Life event combinations */}
          {ad.lifeEvents?.length > 0 && (
            <div className="past-pred-life-events" style={{ marginTop: 8 }}>
              <div style={{ color: '#6b7280', fontSize: 10, fontWeight: 700, letterSpacing: 1, marginBottom: 5 }}>
                LIFE EVENT COMBINATIONS DETECTED
              </div>
              {ad.lifeEvents.map((ev, i) => (
                <LifeEventBadge key={i} event={ev} lang={lang} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MahadashaBlock = ({ md, planetScores, lang, currentPeriod }) => {
  const isCurrentMaha = !!(currentPeriod && currentPeriod.mdPlanet === md.planet);
  const [open, setOpen] = useState(() => isCurrentMaha);
  const score   = planetScores?.[md.planet];
  const quality = score?.normalized || 'average';
  const overview = md.overview?.[lang] || md.overview?.english || '';

  return (
    <div style={{
      marginBottom: 10,
      border: `1px solid ${isCurrentMaha ? '#2563eb' : '#374151'}`,
      borderRadius: 6,
      overflow: 'hidden',
      background: isCurrentMaha ? '#0c1a2e' : '#1a1a1a',
    }}>
      {/* Maha header */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '9px 12px', cursor: 'pointer',
          background: isCurrentMaha ? '#1e293b' : '#111827',
          borderBottom: open ? '1px solid #374151' : 'none',
        }}
        onClick={() => setOpen(v => !v)}
      >
        <span style={{ color: '#6b7280', fontSize: 12, minWidth: 10 }}>{open ? '▾' : '▸'}</span>
        <span style={{ fontSize: 16 }}>{PLANET_ICONS[md.planet] || '●'}</span>
        <span style={{ color: '#f9fafb', fontWeight: 600, fontSize: 14, flex: 1 }}>
          {md.planet} Mahadasha
          <span style={{ color: '#9ca3af', fontWeight: 400, fontSize: 12, marginLeft: 8 }}>
            Age {md.ageStart}–{md.ageEnd}
          </span>
        </span>
        {isCurrentMaha && (
          <span style={{ background: '#1d4ed8', color: '#bfdbfe', fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>
            ● ACTIVE
          </span>
        )}
        <QualityBadge quality={quality} />
        <span style={{ color: '#6b7280', fontSize: 11, minWidth: 40, textAlign: 'right' }}>
          {md.antardashas?.length || 0} AD
        </span>
      </div>

      {open && (
        <div style={{ padding: '8px 12px' }}>
          {overview && (
            <p style={{ color: '#9ca3af', fontSize: 12, lineHeight: 1.6, margin: '0 0 10px 0', whiteSpace: 'pre-line' }}>
              {overview}
            </p>
          )}
          {md.antardashas?.map((ad, i) => {
            const isCurrent = currentPeriod
              && currentPeriod.mdPlanet === ad.mdPlanet
              && currentPeriod.adPlanet === ad.adPlanet;
            return (
              <AntardashaBlock key={i} ad={ad} lang={lang} isCurrent={!!isCurrent} />
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const PastPredictionsReport = ({ onClose }) => {

  const [form, setForm] = useState({
    name: '', dob: '', tob: '10:00',
    place: '',
    lat: 17.3850, lon: 78.4867, tzone: 5.5,
    language: 'english',
  });
  const [geocoding,     setGeocoding]     = useState(false);
  const [geocodeStatus, setGeocodeStatus] = useState('Type place name and click 📍 Lookup');
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState('');
  const [report,        setReport]        = useState(null);
  const [lang,          setLang]          = useState('english');
  const reportRef = useRef(null);

  const setField = useCallback((k, v) => setForm(f => ({ ...f, [k]: v })), []);

  const lookupPlace = async () => {
    if (!form.place.trim()) return;
    setGeocoding(true);
    setGeocodeStatus('Looking up...');
    const coords = await geocodePlace(form.place);
    setForm(f => ({ ...f, lat: coords.lat, lon: coords.lon, tzone: coords.tzone }));
    setGeocodeStatus(coords.display
      ? `✓ ${coords.display} (tz ${coords.tzone})`
      : `✓ lat ${coords.lat.toFixed(3)}, lon ${coords.lon.toFixed(3)}, tz ${coords.tzone}`);
    setGeocoding(false);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.dob) { setError('Date of birth is required'); return; }
    setLoading(true); setError(''); setReport(null);

    try {
      const [yr, mo, dy] = form.dob.split('-').map(Number);
      const [hh, mm]     = (form.tob || '10:00').split(':').map(Number);
      const body = {
        name: form.name || 'Client', day: dy, month: mo, year: yr,
        hour: hh, minutes: mm, seconds: 0,
        latitude: form.lat, longitude: form.lon, timezone: form.tzone,
        place: form.place,
        language: form.language,
      };

      console.log('[PAST-PRED UI] Submitting:', body);
      const res  = await fetch(`${API_BASE}/api/past-predictions/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Generation failed');

      setReport(data.report);
      setLang(form.language);
      console.log('[PAST-PRED UI] Report received. Mahadashas:', data.report.mahadashas?.length);
      setTimeout(() => reportRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (err) {
      console.error('[PAST-PRED UI] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
    <style>{`
      @media print {
        body > * { display: none !important; }
        .past-pred-modal { display: block !important; position: static !important; background: white !important; color: black !important; }
        .past-pred-modal-inner { border: none !important; box-shadow: none !important; max-width: 100% !important; }
        .past-pred-close-btn { display: none !important; }
        .past-pred-form-section { display: none !important; }
        .past-pred-lang-bar { display: none !important; }
        .past-pred-ad-block { break-inside: avoid; display: block !important; }
        .past-pred-life-events { display: block !important; }
        .past-pred-life-event-badge { break-inside: avoid; }
        .past-pred-md-block { break-inside: avoid; }
        .past-pred-print-hidden { display: none !important; }
      }
    `}</style>
    <div
      className="past-pred-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 9000,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        overflowY: 'auto', padding: '20px 12px',
      }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="past-pred-modal-inner" style={{
        background: '#111827', border: '1px solid #374151', borderRadius: 10,
        width: '100%', maxWidth: 820, padding: 0, position: 'relative',
      }}>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 18px', borderBottom: '1px solid #374151',
          background: '#1f2937', borderRadius: '10px 10px 0 0',
        }}>
          <span style={{ fontSize: 18 }}>📊</span>
          <span style={{ color: '#f9fafb', fontWeight: 700, fontSize: 16, flex: 1 }}>
            Past Predictions — Engine Report
          </span>
          <span style={{ color: '#6b7280', fontSize: 11 }}>Pure logic · No AI</span>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: '1px solid #374151', color: '#9ca3af',
              borderRadius: 5, padding: '3px 10px', cursor: 'pointer', fontSize: 14,
            }}
          >✕ Close</button>
        </div>

        {/* Form */}
        <div style={{ padding: '16px 18px', borderBottom: '1px solid #374151' }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>

            {/* Name */}
            <div style={{ flex: '1 1 160px' }}>
              <label style={labelStyle}>Client Name</label>
              <input style={inputStyle} value={form.name}
                onChange={e => setField('name', e.target.value)}
                placeholder="Name (optional)" />
            </div>

            {/* DOB */}
            <div style={{ flex: '1 1 140px' }}>
              <label style={labelStyle}>Date of Birth *</label>
              <input style={inputStyle} type="date" value={form.dob}
                onChange={e => setField('dob', e.target.value)} required />
            </div>

            {/* TOB */}
            <div style={{ flex: '1 1 120px' }}>
              <label style={labelStyle}>Time of Birth</label>
              <input style={inputStyle} type="time" value={form.tob}
                onChange={e => setField('tob', e.target.value)} />
            </div>

            {/* Language */}
            <div style={{ flex: '1 1 140px' }}>
              <label style={labelStyle}>Language</label>
              <select style={inputStyle} value={form.language}
                onChange={e => setField('language', e.target.value)}>
                {LANG_BTNS.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
              </select>
            </div>

            {/* Place + geocode */}
            <div style={{ flex: '1 1 100%' }}>
              <label style={labelStyle}>Place of Birth</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  style={{ ...inputStyle, flex: 1 }}
                  value={form.place}
                  onChange={e => setField('place', e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), lookupPlace())}
                  placeholder="City, Country"
                />
                <button type="button" onClick={lookupPlace} disabled={geocoding} style={btnOutlineStyle}>
                  {geocoding ? '...' : '📍 Lookup'}
                </button>
              </div>
              {geocodeStatus && (
                <div style={{ color: '#6b7280', fontSize: 11, marginTop: 3 }}>
                  {geocodeStatus}
                  <span style={{ marginLeft: 10, color: '#4b5563' }}>
                    lat {form.lat.toFixed(4)}, lon {form.lon.toFixed(4)}, tz {form.tzone}
                  </span>
                </div>
              )}
            </div>

            {/* Submit */}
            <div style={{ flex: '1 1 100%', display: 'flex', gap: 10, alignItems: 'center' }}>
              <button type="submit" disabled={loading} style={btnPrimaryStyle}>
                {loading ? '⏳ Generating...' : '🔮 Generate Report'}
              </button>
              {error && <span style={{ color: '#f87171', fontSize: 12 }}>⚠ {error}</span>}
              {report && !loading && <span style={{ color: '#4ade80', fontSize: 12 }}>✓ Report ready</span>}
            </div>

          </form>
        </div>

        {/* Report */}
        {report && (
          <div ref={reportRef} style={{ padding: '16px 18px' }}>

            {/* Language toggle */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
              {LANG_BTNS.map(l => (
                <button key={l.id} onClick={() => setLang(l.id)} style={{
                  background: lang === l.id ? '#2563eb' : '#1f2937',
                  border: `1px solid ${lang === l.id ? '#3b82f6' : '#374151'}`,
                  color: lang === l.id ? '#fff' : '#9ca3af',
                  borderRadius: 5, padding: '4px 14px', cursor: 'pointer', fontSize: 13,
                }}>
                  {l.label}
                </button>
              ))}
              <span style={{ color: '#6b7280', fontSize: 11, alignSelf: 'center', marginLeft: 8 }}>
                Switch language · No refetch needed
              </span>
            </div>

            {/* Intro */}
            <div style={{
              background: '#1f2937', border: '1px solid #374151',
              borderRadius: 6, padding: 14, marginBottom: 16,
            }}>
              <div style={{ color: '#f9fafb', fontSize: 14, marginBottom: 12 }}>
                {report.intro?.[lang] || report.intro?.english}
              </div>
              <PlanetScoresTable scores={report.planetScores} />
            </div>

            {/* Current period highlight */}
            {report.currentPeriod && (
              <div style={{
                background: '#0c1a2e', border: '2px solid #2563eb',
                borderRadius: 6, padding: 14, marginBottom: 16,
              }}>
                <div style={{ color: '#93c5fd', fontWeight: 700, fontSize: 13, marginBottom: 10 }}>
                  ● CURRENT DASHA PERIOD
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                  <span style={{ background: '#1e3a5f', color: '#93c5fd', padding: '2px 10px', borderRadius: 4, fontSize: 12 }}>
                    {report.currentPeriod.mdPlanet} Maha
                  </span>
                  <span style={{ color: '#6b7280', fontSize: 12, alignSelf: 'center' }}>/</span>
                  <span style={{ background: '#1e293b', color: '#bfdbfe', padding: '2px 10px', borderRadius: 4, fontSize: 12 }}>
                    {report.currentPeriod.adPlanet} Antar
                  </span>
                  <span style={{ color: '#6b7280', fontSize: 12, alignSelf: 'center' }}>
                    Age {report.currentPeriod.ageStart}–{report.currentPeriod.ageEnd}
                  </span>
                  <QualityBadge quality={report.currentPeriod.combinedQuality} />
                  {report.currentPeriod.sadeSati && (
                    <span style={{ background: '#7f1d1d', color: '#fca5a5', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>
                      ⚠ Sade Sati Running
                    </span>
                  )}
                </div>
                {report.currentPeriod.activatedHouses?.length > 0 && (
                  <div style={{ marginBottom: 10, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <span style={{ color: '#6b7280', fontSize: 11 }}>Houses: </span>
                    {report.currentPeriod.activatedHouses.map(h => (
                      <HousePill key={h} house={h} overlap={report.currentPeriod.overlappingHouses?.includes(h)} />
                    ))}
                  </div>
                )}
                <div style={{
                  color: '#d1d5db', fontSize: 13, lineHeight: 1.75,
                  whiteSpace: 'pre-line', borderLeft: '2px solid #2563eb', paddingLeft: 12,
                }}>
                  {report.currentPeriod.text?.[lang] || report.currentPeriod.text?.english}
                </div>
              </div>
            )}

            {/* Full dasha history */}
            <div style={{ color: '#9ca3af', fontSize: 12, marginBottom: 10, fontWeight: 600, letterSpacing: 1 }}>
              COMPLETE DASHA REPORT — Click mahadasha to expand
            </div>
            {report.mahadashas?.map((md, i) => (
              <MahadashaBlock
                key={i} md={md}
                planetScores={report.planetScores}
                lang={lang}
                currentPeriod={report.currentPeriod}
              />
            ))}

            <div style={{ color: '#374151', fontSize: 11, textAlign: 'center', marginTop: 16, paddingTop: 16, borderTop: '1px solid #1f2937' }}>
              VTD Astrology · Engine Report · Internal Use Only
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

// ─── Shared styles ────────────────────────────────────────────────────────────

const labelStyle = {
  display: 'block', color: '#9ca3af', fontSize: 11, marginBottom: 4, fontWeight: 500,
};
const inputStyle = {
  width: '100%', background: '#1f2937', border: '1px solid #374151',
  borderRadius: 5, padding: '6px 10px', color: '#f9fafb', fontSize: 13,
  boxSizing: 'border-box', outline: 'none',
};
const btnPrimaryStyle = {
  background: '#2563eb', border: 'none', color: '#fff', borderRadius: 5,
  padding: '8px 20px', cursor: 'pointer', fontSize: 14, fontWeight: 600,
};
const btnOutlineStyle = {
  background: '#1f2937', border: '1px solid #374151', color: '#d1d5db',
  borderRadius: 5, padding: '6px 12px', cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap',
};

export default PastPredictionsReport;
